import { ReactNode, useEffect, useMemo } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import LoadingButton from '@mui/lab/LoadingButton';
import { Box, InputAdornment, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { FormState, useForm, UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { User } from '../../../api/gql';
import { FormInput, FormInputProps } from '../../../components/form/FormInput';
import { useAresSearch } from '../../../hooks/useAresSearch';
import { userValidationSchema } from '../../../utils/validationSchemas';

export type UserFormType = Omit<User, '__typename' | 'defaultRemindingTone' | 'plan' | 'email' | 'id' | 'role'>;

type Props = {
  onSubmit: (data: UserFormType) => void;
  readonly?: boolean;
  defaultValues?: UserFormType;
  displayAresBtn?: boolean;
  values?: UserFormType;
  renderActions?: (formState: FormState<UserFormType>, reset: UseFormReset<UserFormType>) => ReactNode;
};
type UserFormProps = Props & Omit<JSX.IntrinsicElements['form'], 'onSubmit'>;

export const UserForm = ({
  onSubmit,
  readonly,
  values,
  renderActions,
  defaultValues,
  displayAresBtn,
  ...rest
}: UserFormProps) => {
  const { t } = useTranslation();
  const { control, reset, formState, setFocus, handleSubmit, getFieldState, getValues, setValue } =
    useForm<UserFormType>({
      defaultValues,
      resolver: yupResolver(userValidationSchema),
    });

  const vatIdFieldState = getFieldState('vatId', formState);
  const { search, state } = useAresSearch();

  const sharedInputProps: Pick<FormInputProps, 'variant' | 'InputProps' | 'control'> = useMemo(
    () => ({
      variant: 'standard',
      InputProps: { readOnly: readonly, disableUnderline: readonly },
      control: control,
    }),
    [control, readonly],
  );

  useEffect(() => {
    if (values) {
      reset(values);
    }
  }, [reset, values]);

  useEffect(() => {
    if (!readonly) {
      setTimeout(() => setFocus('firstName'));
    }
  }, [readonly, setFocus]);

  const renderLabel = (transKey: string, required?: boolean) => {
    const string = [t(transKey)];
    if (required && !readonly) {
      string.push('*');
    }
    return string.join('');
  };

  const searchAres = async () => {
    const vatId = getValues('vatId');

    if (!vatId) {
      return;
    }

    // try look-up customer in ARES
    const aresLookup = await search({ vatId: vatId });

    if (aresLookup) {
      aresLookup.queryAres?.taxId
        ? setValue('taxId', aresLookup.queryAres.taxId, {
            shouldValidate: true,
          })
        : null;
      aresLookup.queryAres?.name
        ? setValue('businessName', aresLookup.queryAres.name, {
            shouldValidate: true,
          })
        : null;
      aresLookup.queryAres?.streetAddress
        ? setValue('streetAddress', aresLookup.queryAres.streetAddress, {
            shouldValidate: true,
          })
        : null;
      aresLookup.queryAres?.city ? setValue('city', aresLookup.queryAres.city, { shouldValidate: true }) : null;
      aresLookup.queryAres?.zip
        ? setValue('zipCode', aresLookup.queryAres.zip, {
            shouldValidate: true,
          })
        : null;
      aresLookup.queryAres?.country
        ? setValue('country', aresLookup.queryAres.country, {
            shouldValidate: true,
          })
        : null;
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} {...rest}>
      <Box>
        <Typography variant={'h6'}>{t('contactInfo')}</Typography>
        <Grid container spacing={2} mt={1}>
          <Grid xs={12} md={6}>
            <FormInput {...sharedInputProps} name={'firstName'} label={renderLabel('firstName', true)} />
          </Grid>
          <Grid xs={12} md={6}>
            <FormInput {...sharedInputProps} name={'lastName'} label={renderLabel('lastName', true)} />
          </Grid>
          <Grid xs={12}>
            <FormInput {...sharedInputProps} name={'phoneNumber'} label={renderLabel('phone', true)} type={'tel'} />
          </Grid>
        </Grid>
      </Box>
      <Box pt={4}>
        <Typography variant={'h6'}>{t('billingInfo')}</Typography>
        <Grid container spacing={2} mt={1}>
          <Grid xs={displayAresBtn ? 8 : 12} md={displayAresBtn ? 8 : 12}>
            <FormInput {...sharedInputProps} name={'vatId'} label={renderLabel('vatId', false)} />
          </Grid>
          {displayAresBtn ? (
            <Grid xs={4}>
              <LoadingButton
                fullWidth
                variant={'outlined'}
                loading={state.isPending}
                onClick={searchAres}
                disabled={vatIdFieldState.invalid}
              >
                {t('loadFromAres')}
              </LoadingButton>
            </Grid>
          ) : null}
          <Grid xs={12} md={6}>
            <FormInput {...sharedInputProps} name={'businessName'} label={renderLabel('businessName', true)} />
          </Grid>
          <Grid xs={12} md={6}>
            <FormInput {...sharedInputProps} name={'taxId'} label={renderLabel('taxId')} />
          </Grid>
          <Grid xs={12} md={6}>
            <FormInput {...sharedInputProps} name={'streetAddress'} label={renderLabel('streetAddress', true)} />
          </Grid>
          <Grid xs={12} md={6}>
            <FormInput {...sharedInputProps} name={'city'} label={renderLabel('city', true)} />
          </Grid>
          <Grid xs={12} md={6}>
            <FormInput {...sharedInputProps} name={'zipCode'} label={renderLabel('zipCode', true)} />
          </Grid>
          <Grid xs={12} md={6}>
            <FormInput {...sharedInputProps} name={'country'} label={renderLabel('country', true)} />
          </Grid>
        </Grid>
      </Box>
      <Box pt={4}>
        <Typography variant={'h6'}>{t('paymentInfo')}</Typography>
        <Grid container spacing={2} mt={1}>
          <Grid xs={12} container spacing={2}>
            <Grid xs={8}>
              <FormInput
                {...sharedInputProps}
                control={control}
                name={'bankAccount'}
                label={t('bankAccount')}
                InputProps={{
                  ...sharedInputProps.InputProps,
                  endAdornment: <InputAdornment position={'end'}>/</InputAdornment>,
                }}
              />
            </Grid>
            <Grid xs={4}>
              <FormInput {...sharedInputProps} control={control} name={'bankNumber'} label={t('bankNumber')} />
            </Grid>
            <Grid xs={12} md={8}>
              <FormInput {...sharedInputProps} control={control} name={'iban'} label={t('iban')} />
            </Grid>
            <Grid xs={12} md={4}>
              <FormInput {...sharedInputProps} control={control} name={'swift'} label={t('swift')} />
            </Grid>
          </Grid>
        </Grid>
      </Box>
      {renderActions ? renderActions(formState, reset) : null}
    </form>
  );
};
